import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListMailPendingActions,
  useListMailAccounts,
  useListProjects,
  useCorrectMailMessageTriage,
  useMarkMailMessageReplied,
  useCreateTask,
  useListMailMessageSuggestions,
  useAcceptSuggestion,
  useRejectSuggestion,
  getListMailPendingActionsQueryKey,
  getGetMailPendingActionsSummaryQueryKey,
  getGetMailUnreadSummaryQueryKey,
  getGetTodayQueryKey,
  getListProjectsQueryKey,
  getListMailMessageSuggestionsQueryKey,
  type MailMessage,
  type MailAccount,
  type Project,
  type SuggestedAction,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CheckSquare,
  Reply,
  EyeOff,
  Sparkles,
  Mail as MailIcon,
  AlertCircle,
  Loader2,
  StickyNote,
  FolderKanban,
  X,
} from "lucide-react";
import { format, isToday, isYesterday } from "date-fns";
import { fr, enUS, type Locale } from "date-fns/locale";
import { useToast } from "@/hooks/use-toast";
import { TriageBadge } from "@/components/triage-badge";
import { ActionDetailDialog } from "@/components/action-detail-dialog";
import CreateTaskDialog, {
  type CreateTaskValues,
} from "@/components/create-task-dialog";
import { useListMailMessageNotes, getListMailMessageNotesQueryKey } from "@workspace/api-client-react";
import { useDemoMode } from "@/hooks/use-demo-mode";

function useInView<T extends Element>(rootMargin = "200px"): {
  ref: (node: T | null) => void;
  inView: boolean;
} {
  const [inView, setInView] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const nodeRef = useRef<T | null>(null);

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  const ref = (node: T | null) => {
    if (observerRef.current && nodeRef.current) {
      observerRef.current.unobserve(nodeRef.current);
    }
    nodeRef.current = node;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              setInView(true);
              observerRef.current?.disconnect();
              break;
            }
          }
        },
        { rootMargin },
      );
    }
    observerRef.current.observe(node);
  };

  return { ref, inView };
}

function suggestionTitle(s: SuggestedAction, fallback: string): string {
  const payload = (s.payload ?? {}) as { title?: string };
  return payload.title?.trim() || fallback;
}

function formatMsgDate(
  raw: string | undefined | null,
  yesterdayLabel: string,
  dateLocale: Locale,
): string {
  if (!raw) return "";
  const d = new Date(raw);
  if (isToday(d)) return format(d, "HH:mm");
  if (isYesterday(d)) return yesterdayLabel;
  return format(d, "d MMM yyyy", { locale: dateLocale });
}

function senderInitial(
  name: string | undefined | null,
  email: string | undefined | null,
): string {
  const src = name || email || "?";
  const trimmed = src.trim();
  if (!trimmed) return "?";
  return trimmed.charAt(0).toUpperCase();
}

function buildActionTitle(message: MailMessage, noSubjectLabel: string): string {
  const subject = message.subject?.trim();
  if (subject) return subject;
  const summary = message.triageSummary?.trim();
  if (summary) return summary.slice(0, 140);
  return noSubjectLabel;
}

function buildActionDescription(
  message: MailMessage,
  fromPrefix: string,
  accountPrefix: string,
  receivedPrefix: string,
  dateLocale: Locale,
  account?: MailAccount,
): string {
  const parts: string[] = [];
  const sender = message.fromName
    ? `${message.fromName}${message.fromAddress ? ` <${message.fromAddress}>` : ""}`
    : (message.fromAddress ?? "");
  if (sender) parts.push(fromPrefix.replace("{{sender}}", sender));
  if (account) parts.push(accountPrefix.replace("{{label}}", account.label));
  if (message.receivedAt) {
    parts.push(receivedPrefix.replace("{{date}}", format(new Date(message.receivedAt), "Pp", { locale: dateLocale })));
  }
  if (message.triageSummary && message.triageSummary !== message.subject) {
    parts.push("");
    parts.push(message.triageSummary);
  }
  if (message.snippet) {
    parts.push("");
    parts.push(message.snippet);
  }
  return parts.join("\n");
}

export default function ActionsPage() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "en" ? enUS : fr;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const [showNoise, setShowNoise] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [openMessageId, setOpenMessageId] = useState<string | null>(null);
  // The mail whose "Créer une tâche" modal is currently open (null = closed).
  const [taskDialogMessage, setTaskDialogMessage] = useState<MailMessage | null>(
    null,
  );

  const pendingQueryKey = getListMailPendingActionsQueryKey({
    includeNoise: showNoise,
  });
  const summaryQueryKey = getGetMailPendingActionsSummaryQueryKey();

  const { data: messages, isLoading } = useListMailPendingActions(
    { includeNoise: showNoise },
    {
      query: {
        queryKey: pendingQueryKey,
        refetchInterval: 60_000,
      },
    },
  );

  const { data: accounts } = useListMailAccounts();
  const accountMap = useMemo(() => {
    const map = new Map<string, MailAccount>();
    for (const a of accounts ?? []) map.set(a.id, a);
    return map;
  }, [accounts]);

  const refreshAfterAction = () => {
    queryClient.invalidateQueries({ queryKey: pendingQueryKey });
    queryClient.invalidateQueries({ queryKey: summaryQueryKey });
    queryClient.invalidateQueries({
      queryKey: getGetMailUnreadSummaryQueryKey(),
    });
    queryClient.invalidateQueries({ queryKey: getGetTodayQueryKey() });
  };

  const createTask = useCreateTask();
  const markReplied = useMarkMailMessageReplied();
  const correct = useCorrectMailMessageTriage();

  const projectsQuery = useListProjects(
    { status: "active" },
    {
      query: {
        queryKey: getListProjectsQueryKey({ status: "active" }),
        staleTime: 60_000,
      },
    },
  );
  const projects: Project[] = projectsQuery.data ?? [];

  const [taskProjectByMessage, setTaskProjectByMessage] = useState<
    Record<string, string>
  >({});

  // Build the pre-filled values for the "Créer une tâche" modal from a mail.
  const buildTaskInitial = (m: MailMessage): CreateTaskValues => ({
    title: buildActionTitle(m, t("common.no_subject")),
    description: buildActionDescription(
      m,
      t("actions_page.from_prefix"),
      t("actions_page.account_prefix"),
      t("actions_page.received_prefix"),
      dateLocale,
      accountMap.get(m.accountId),
    ),
    projectId: taskProjectByMessage[m.id] || "",
    priority: "medium",
    dueDate: "",
  });

  // Actually create the task once the user has reviewed/edited it in the modal.
  const submitCreateTask = async (m: MailMessage, values: CreateTaskValues) => {
    setBusyId(m.id);
    try {
      await createTask.mutateAsync({
        data: {
          title: values.title,
          description: values.description || null,
          priority: values.priority as "low" | "medium" | "high" | "urgent",
          ...(values.projectId ? { projectId: values.projectId } : {}),
          ...(values.dueDate
            ? { dueDate: new Date(`${values.dueDate}T12:00:00`).toISOString() }
            : {}),
        },
      });
      // Once a task is created, the mail is "handled" → mark as replied so
      // it disappears from the actions list.
      await markReplied.mutateAsync({ id: m.id });
      refreshAfterAction();
      setTaskDialogMessage(null);
      toast({ title: t("actions_page.task_created_toast") });
    } catch (e) {
      toast({
        title: t("actions_page.task_create_error"),
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleMarkReplied = async (m: MailMessage) => {
    setBusyId(m.id);
    try {
      await markReplied.mutateAsync({ id: m.id });
      refreshAfterAction();
      toast({ title: t("actions_page.marked_replied_toast") });
    } catch (e) {
      toast({
        title: t("actions_page.replied_error"),
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setBusyId(null);
    }
  };

  // "Déplacer dans le bruit" : ce mail est du bruit (newsletter, notif auto,
  // erreur…) → on le reclasse en "noise" pour entraîner le filtre.
  const handleMoveToNoise = async (m: MailMessage) => {
    setBusyId(m.id);
    try {
      await correct.mutateAsync({
        id: m.id,
        data: { category: "noise" },
      });
      refreshAfterAction();
      toast({ title: t("actions_page.moved_to_noise_toast") });
    } catch (e) {
      toast({
        title: t("actions_page.moved_error"),
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setBusyId(null);
    }
  };

  // "Ignorer" : ce mail ne demande pas d'action de ma part, mais ce n'est PAS
  // du bruit → on le reclasse en "fyi" (pour information). Il quitte la liste
  // À faire sans entraîner le filtre anti-bruit.
  const handleDismiss = async (m: MailMessage) => {
    setBusyId(m.id);
    try {
      await correct.mutateAsync({
        id: m.id,
        data: { category: "fyi" },
      });
      refreshAfterAction();
      toast({ title: t("actions_page.dismissed_toast") });
    } catch (e) {
      toast({
        title: t("actions_page.moved_error"),
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setBusyId(null);
    }
  };

  const visibleMessages = messages ?? [];
  const actionMessages = visibleMessages.filter(
    (m) => m.triageCategory === "action_required",
  );
  const noiseMessages = visibleMessages.filter(
    (m) => m.triageCategory === "noise",
  );

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <header className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <CheckSquare className="w-5 h-5 text-primary" />
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
            {t("actions_page.title")}
          </h1>
        </div>
        <p className="text-sm text-muted-foreground">
          {t("actions_page.subtitle")}
        </p>
      </header>

      <div className="flex items-center justify-between mb-4 px-1">
        <div className="text-xs text-muted-foreground">
          {isLoading ? (
            <Skeleton className="h-3 w-32" />
          ) : (
            <>
              {t(actionMessages.length === 1 ? "actions_page.pending_one" : "actions_page.pending_other", { count: actionMessages.length })}
              {showNoise && " " + t(noiseMessages.length === 1 ? "actions_page.noise_count_one" : "actions_page.noise_count_other", { count: noiseMessages.length })}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id="toggle-noise"
            checked={showNoise}
            onCheckedChange={setShowNoise}
            data-testid="toggle-show-noise"
          />
          <Label
            htmlFor="toggle-noise"
            className="text-xs text-muted-foreground cursor-pointer select-none"
          >
            {t("actions_page.show_noise")}
          </Label>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full rounded-xl" />
          ))}
        </div>
      ) : visibleMessages.length === 0 ? (
        <EmptyState showNoise={showNoise} />
      ) : (
        <ul className="space-y-2" data-testid="actions-list">
          {visibleMessages.map((m) => (
            <ActionCard
              key={m.id}
              message={m}
              account={accountMap.get(m.accountId)}
              busy={busyId === m.id}
              projects={projects}
              selectedProjectId={taskProjectByMessage[m.id] ?? ""}
              onSelectProject={(projectId) =>
                setTaskProjectByMessage((prev) => ({
                  ...prev,
                  [m.id]: projectId,
                }))
              }
              onOpen={() => setOpenMessageId(m.id)}
              onViewMail={() => setLocation(`/app/mail?id=${encodeURIComponent(m.id)}`)}
              onCreateTask={() => setTaskDialogMessage(m)}
              onMarkReplied={() => handleMarkReplied(m)}
              onDismiss={() => handleDismiss(m)}
              onMoveToNoise={() => handleMoveToNoise(m)}
              onAfterSuggestionAction={refreshAfterAction}
            />
          ))}
        </ul>
      )}

      <ActionDetailDialog
        open={openMessageId !== null}
        onOpenChange={(o) => {
          if (!o) setOpenMessageId(null);
        }}
        message={visibleMessages.find((m) => m.id === openMessageId) ?? null}
        account={
          openMessageId
            ? accountMap.get(
                visibleMessages.find((m) => m.id === openMessageId)?.accountId ?? "",
              )
            : undefined
        }
        busy={busyId === openMessageId}
        onCreateTask={() => {
          const m = visibleMessages.find((mm) => mm.id === openMessageId);
          if (!m) return;
          setOpenMessageId(null);
          setTaskDialogMessage(m);
        }}
        onOpenMail={() => {
          if (openMessageId) {
            setLocation(`/app/mail?id=${encodeURIComponent(openMessageId)}`);
          }
        }}
      />

      {taskDialogMessage && (
        <CreateTaskDialog
          open={taskDialogMessage !== null}
          onOpenChange={(o) => {
            if (!o) setTaskDialogMessage(null);
          }}
          initial={buildTaskInitial(taskDialogMessage)}
          projects={projects}
          busy={busyId === taskDialogMessage.id}
          onSubmit={(values) => submitCreateTask(taskDialogMessage, values)}
        />
      )}
    </div>
  );
}

function EmptyState({ showNoise }: { showNoise: boolean }) {
  const { t } = useTranslation();

  return (
    <div
      className="flex flex-col items-center justify-center text-center py-16 border border-dashed border-border rounded-xl bg-muted/20"
      data-testid="actions-empty"
    >
      <Sparkles className="w-10 h-10 text-primary/40 mb-3" />
      <p className="text-sm font-medium text-foreground">{t("actions_page.empty_title")}</p>
      <p className="text-xs text-muted-foreground mt-1 max-w-xs">
        {showNoise
          ? t("actions_page.empty_noise_desc")
          : t("actions_page.empty_desc")}
      </p>
    </div>
  );
}

function ActionCard({
  message,
  account,
  busy,
  projects,
  selectedProjectId,
  onSelectProject,
  onOpen,
  onViewMail,
  onCreateTask,
  onMarkReplied,
  onDismiss,
  onMoveToNoise,
  onAfterSuggestionAction,
}: {
  message: MailMessage;
  account: MailAccount | undefined;
  busy: boolean;
  projects: Project[];
  selectedProjectId: string;
  onSelectProject: (projectId: string) => void;
  onOpen: () => void;
  onViewMail: () => void;
  onCreateTask: () => void;
  onMarkReplied: () => void;
  onDismiss: () => void;
  onMoveToNoise: () => void;
  onAfterSuggestionAction: () => void;
}) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "en" ? enUS : fr;
  const NO_PROJECT = "__none__";
  const initial = senderInitial(message.fromName, message.fromAddress);
  const isNoise = message.triageCategory === "noise";
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Lightweight, cache-friendly fetch so the card can show an annotation
  // count badge. The dialog re-uses the same query key, so this never
  // double-fetches once the dialog has been opened.
  const { data: notesData } = useListMailMessageNotes(message.id, {
    query: {
      queryKey: getListMailMessageNotesQueryKey(message.id),
      staleTime: 60_000,
    },
  });
  const notesCount = notesData?.notes.length ?? 0;

  // Lazy-fetch AI suggestions only once the card scrolls near the viewport.
  // This avoids hitting the suggestions endpoint for every off-screen card.
  const { ref: inViewRef, inView } = useInView<HTMLLIElement>("200px");
  const suggestionsEnabled = inView && Boolean(message.triageCategory) && !isNoise;
  const { data: suggestionsData } = useListMailMessageSuggestions(message.id, {
    query: {
      enabled: suggestionsEnabled,
      queryKey: getListMailMessageSuggestionsQueryKey(message.id),
      staleTime: 60_000,
    },
  });
  const suggestions = (suggestionsData ?? []) as SuggestedAction[];
  // Show only the top 2 suggestions inline to keep the card compact.
  const topSuggestions = suggestions.slice(0, 2);

  const acceptSuggestion = useAcceptSuggestion();
  const rejectSuggestion = useRejectSuggestion();
  const markRepliedAfterAccept = useMarkMailMessageReplied();
  const [pendingSuggestionId, setPendingSuggestionId] = useState<string | null>(null);

  const invalidateSuggestionList = () => {
    queryClient.invalidateQueries({
      queryKey: getListMailMessageSuggestionsQueryKey(message.id),
    });
  };

  const handleAcceptSuggestion = async (s: SuggestedAction) => {
    setPendingSuggestionId(s.id);
    try {
      await acceptSuggestion.mutateAsync({ id: s.id });
      // Match the "Créer une tâche" flow: once the mail has produced an
      // action, mark it as replied so it disappears from À faire.
      await markRepliedAfterAccept.mutateAsync({ id: message.id });
      invalidateSuggestionList();
      onAfterSuggestionAction();
      toast({ title: t("actions_page.suggestion_accepted_toast") });
    } catch (e) {
      toast({
        title: t("actions_page.suggestion_create_error"),
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setPendingSuggestionId(null);
    }
  };

  const handleRejectSuggestion = async (s: SuggestedAction) => {
    setPendingSuggestionId(s.id);
    try {
      await rejectSuggestion.mutateAsync({ id: s.id });
      invalidateSuggestionList();
    } catch (e) {
      toast({
        title: t("actions_page.suggestion_reject_error"),
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setPendingSuggestionId(null);
    }
  };

  return (
    <li
      ref={inViewRef}
      className={`group flex flex-col gap-3 rounded-xl border bg-card p-4 transition-colors ${
        isNoise
          ? "border-border/60 opacity-70"
          : "border-border hover:border-primary/40"
      }`}
      data-testid={`action-row-${message.id}`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-sm font-semibold ${
            isNoise
              ? "bg-muted text-muted-foreground"
              : "bg-primary/10 text-primary"
          }`}
        >
          {initial}
        </div>

        <button
          type="button"
          onClick={onOpen}
          className="flex-1 min-w-0 text-left"
          data-testid={`action-open-${message.id}`}
        >
          <div className="flex items-baseline justify-between gap-2 mb-0.5">
            <span className="text-sm font-medium text-foreground truncate">
              {message.fromName ||
                message.fromAddress ||
                t("actions_page.unknown_sender")}
            </span>
            <span className="text-[11px] text-muted-foreground shrink-0">
              {formatMsgDate(message.receivedAt, t("common.yesterday"), dateLocale)}
            </span>
          </div>
          <p className="text-sm font-semibold text-foreground line-clamp-2">
            {message.subject || t("common.no_subject")}
          </p>
          {message.triageSummary && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
              {message.triageSummary}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            <TriageBadge category={message.triageCategory} short />
            {account && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">
                <MailIcon className="w-3 h-3" />
                {account.label}
              </span>
            )}
            {message.triageProjectHint && (
              <span className="text-[10px] text-muted-foreground truncate">
                · {message.triageProjectHint}
              </span>
            )}
            {typeof message.triageConfidence === "number" && (
              <span className="text-[10px] text-muted-foreground">
                · {Math.round(message.triageConfidence * 100)}%
              </span>
            )}
            {notesCount > 0 && (
              <span
                className="inline-flex items-center gap-1 text-[10px] text-primary font-medium"
                data-testid={`action-notes-count-${message.id}`}
              >
                · <StickyNote className="w-3 h-3" />
                {notesCount}
              </span>
            )}
          </div>
        </button>
      </div>

      {topSuggestions.length > 0 && (
        <div
          className="flex flex-col gap-1.5 rounded-lg border border-primary/20 bg-primary/5 p-2"
          data-testid={`action-suggestions-${message.id}`}
        >
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold text-primary">
            <Sparkles className="w-3 h-3" />
            {t("actions_page.ai_suggestions")}
          </div>
          {topSuggestions.map((s) => {
            const pending = pendingSuggestionId === s.id;
            return (
              <div
                key={s.id}
                className="flex items-center justify-between gap-2 rounded-md bg-background/80 border border-border/60 px-2 py-1.5"
                data-testid={`action-suggestion-${s.id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-foreground truncate">
                    {suggestionTitle(s, t("common.suggested_task"))}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {t(`item_type.${s.type}`, s.type)}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    size="sm"
                    variant="default"
                    className="h-7 px-2 text-xs"
                    onClick={() => handleAcceptSuggestion(s)}
                    disabled={pending || busy}
                    data-testid={`action-suggestion-accept-${s.id}`}
                  >
                    {pending ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <CheckSquare className="w-3 h-3 mr-1" />
                    )}
                    {t("actions_page.accept_btn")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0"
                    onClick={() => handleRejectSuggestion(s)}
                    disabled={pending || busy}
                    aria-label={t("actions_page.ignore_suggestion_aria")}
                    data-testid={`action-suggestion-reject-${s.id}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2 pt-1 border-t border-border/40">
        <Select
          value={selectedProjectId || NO_PROJECT}
          onValueChange={(v) =>
            onSelectProject(v === NO_PROJECT ? "" : v)
          }
        >
          <SelectTrigger
            className="h-8 w-[170px] text-xs"
            aria-label={t("actions_page.project_aria")}
            data-testid={`select-action-project-${message.id}`}
          >
            <FolderKanban className="w-3 h-3 mr-1 shrink-0 text-muted-foreground" />
            <SelectValue placeholder={t("actions_page.no_project")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_PROJECT}>{t("actions_page.no_project")}</SelectItem>
            {projects.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="default"
          size="sm"
          onClick={onCreateTask}
          disabled={busy}
          data-testid={`action-create-task-${message.id}`}
        >
          {busy ? (
            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
          ) : (
            <CheckSquare className="w-3.5 h-3.5 mr-1.5" />
          )}
          {t("actions_page.create_task_btn")}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onViewMail}
          data-testid={`action-view-mail-${message.id}`}
        >
          <MailIcon className="w-3.5 h-3.5 mr-1.5" />
          {t("actions_page.view_mail_btn")}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onMarkReplied}
          disabled={busy}
          data-testid={`action-mark-replied-${message.id}`}
        >
          <Reply className="w-3.5 h-3.5 mr-1.5" />
          {t("actions_page.replied_btn")}
        </Button>
        {isNoise ? (
          <Button
            variant="ghost"
            size="sm"
            disabled
            data-testid={`action-noise-badge-${message.id}`}
          >
            <AlertCircle className="w-3.5 h-3.5 mr-1.5" />
            {t("actions_page.noise_badge")}
          </Button>
        ) : (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={onDismiss}
              disabled={busy}
              title={t("actions_page.dismiss_hint")}
              data-testid={`action-dismiss-${message.id}`}
            >
              <X className="w-3.5 h-3.5 mr-1.5" />
              {t("actions_page.dismiss_btn")}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onMoveToNoise}
              disabled={busy}
              title={t("actions_page.ignore_hint")}
              data-testid={`action-ignore-${message.id}`}
            >
              <EyeOff className="w-3.5 h-3.5 mr-1.5" />
              {t("actions_page.ignore_btn")}
            </Button>
          </>
        )}
      </div>
    </li>
  );
}

